import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { apiGet } from './apiUtils';
import { getCurrentUser } from './authUtils';
import { Button } from './components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './components/ui/card';
import { AlertCircle, Loader2, TrendingUp, TrendingDown } from 'lucide-react';
import { Alert, AlertDescription } from './components/ui/alert';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableFooter } from './components/ui/table';
import DateRangeSelector from './components/DateRangeSelector';
import { useDateRangeSelector } from './hooks/useDateRangeSelector';

function IncomeExpensesReport() {
  // Use the date range selector hook
  const dateSelector = useDateRangeSelector();
  
  // Destructure for easier access
  const { reportType, selectedYear, selectedMonth, selectedWeek, weeksInMonth, selectedDate, customStartDate, customEndDate } = dateSelector;
  
  // Component-specific state
  const [incomeData, setIncomeData] = useState([]);
  const [expensesData, setExpensesData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [generating, setGenerating] = useState(false);

  // User data
  const userData = useMemo(() => getCurrentUser(), []);
  // Token no longer needed - apiUtils handles authentication automatically
  const shopName = useMemo(() => userData.shopName || 'Easy Sheets, Daily', [userData.shopName]);

  // Category ordering (to preserve original entry order from category master)
  const [incomeCategoryOrderMap, setIncomeCategoryOrderMap] = useState({});
  const normalizeCategoryName = (val) => (val || '').toString().trim().toLowerCase();

  // Load income categories (defines canonical order used in entry screen)
  useEffect(() => {
    const loadIncomeCategories = async () => {
      try {
        const res = await apiGet('/api/income-expenses/income-categories');
        const categories = await res.json();
          // Build order map: lower index means earlier in list
          const map = {};
          (categories || []).forEach((c, idx) => {
            const raw = (c?.name || c)?.toString();
            const key = normalizeCategoryName(raw);
            if (key) map[key] = idx;
          });
          setIncomeCategoryOrderMap(map);
      } catch (_) {}
    };
    loadIncomeCategories();
  }, []);



  // Fetch data for the selected date range
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const { startDate, endDate } = dateSelector.getDateRange();
      
      // Generate array of dates in range
      const dates = [];
      const current = new Date(startDate);
      const end = new Date(endDate);
      
      while (current <= end) {
        dates.push(current.toLocaleDateString('en-CA'));
        current.setDate(current.getDate() + 1);
      }
      
      // Fetch data for all dates in parallel
      const incomePromises = dates.map(async date => {
        try {
          const res = await apiGet(`/api/income-expenses/income?date=${date}`);
          const data = await res.json();
          return Array.isArray(data) ? data : [];
        } catch (error) {
          console.error(`Error fetching income for ${date}:`, error);
          return [];
        }
      });
      
      const expensesPromises = dates.map(async date => {
        try {
          const res = await apiGet(`/api/income-expenses/expenses?date=${date}`);
          const data = await res.json();
          return Array.isArray(data) ? data : [];
        } catch (error) {
          console.error(`Error fetching expenses for ${date}:`, error);
          return [];
        }
      });
      
      const [incomeResults, expensesResults] = await Promise.all([
        Promise.all(incomePromises),
        Promise.all(expensesPromises)
      ]);
      
      // Flatten and combine results with dates
      const allIncome = incomeResults.flatMap((dayIncome, index) => {
        if (!Array.isArray(dayIncome)) return [];
        return dayIncome.map(item => ({ ...item, date: dates[index] }));
      });
      
      const allExpenses = expensesResults.flatMap((dayExpenses, index) => {
        if (!Array.isArray(dayExpenses)) return [];
        return dayExpenses.map(item => ({ ...item, date: dates[index] }));
      });
      
      setIncomeData(allIncome);
      setExpensesData(allExpenses);
      
    } catch (err) {
      setError(err.message);
      console.error('Error fetching income/expenses data:', err);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportType, selectedDate, selectedYear, selectedMonth, selectedWeek, customStartDate, customEndDate]);

  // Fetch data when parameters change
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Calculate totals
  const totals = useMemo(() => {
    const totalIncome = incomeData.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
    const totalExpenses = expensesData.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
    return { totalIncome, totalExpenses };
  }, [incomeData, expensesData]);

  // Aggregations by category/source for weekly, monthly, yearly, custom
  const aggregatedIncomeByCategory = useMemo(() => {
    if (!(reportType === 'weekly' || reportType === 'monthly' || reportType === 'yearly' || reportType === 'custom')) return [];
    const map = new Map();
    (incomeData || []).forEach(item => {
      const key = normalizeCategoryName(item.source || item.category || 'N/A');
      const amt = parseFloat(item.amount) || 0;
      map.set(key, (map.get(key) || 0) + amt);
    });
    const list = Array.from(map.entries()).map(([key, amount]) => ({ category: key, amount }));
    // If we have a canonical order from categories, sort by it; otherwise keep insertion order
    const preferred = ['sitting', 'used bottles/cartons sale', 'others'];
    list.sort((a, b) => {
      const aiPref = preferred.indexOf(a.category);
      const biPref = preferred.indexOf(b.category);
      if (aiPref !== -1 || biPref !== -1) {
        return (aiPref === -1 ? Number.MAX_SAFE_INTEGER : aiPref) - (biPref === -1 ? Number.MAX_SAFE_INTEGER : biPref);
      }
      if (incomeCategoryOrderMap && Object.keys(incomeCategoryOrderMap).length > 0) {
        const ai = incomeCategoryOrderMap[a.category] ?? Number.MAX_SAFE_INTEGER;
        const bi = incomeCategoryOrderMap[b.category] ?? Number.MAX_SAFE_INTEGER;
        return ai - bi;
      }
      return 0;
    });
    // Pretty label: title case for display if needed
    return list.map(it => ({
      category: it.category.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
      amount: it.amount
    }));
  }, [reportType, incomeData, incomeCategoryOrderMap]);

  // Daily income ordering by canonical order (no aggregation)
  const orderedDailyIncome = useMemo(() => {
    if (reportType !== 'daily') return incomeData;
    const preferred = ['sitting', 'used bottles/cartons sale', 'others'];
    const getIndex = (name) => {
      const key = normalizeCategoryName(name || '');
      const pref = preferred.indexOf(key);
      if (pref !== -1) return pref;
      const ord = incomeCategoryOrderMap[key];
      return ord !== undefined ? ord : Number.MAX_SAFE_INTEGER;
    };
    return [...incomeData].sort((a, b) => getIndex(a.source) - getIndex(b.source));
  }, [reportType, incomeData, incomeCategoryOrderMap]);

  const aggregatedExpensesByCategory = useMemo(() => {
    if (!(reportType === 'weekly' || reportType === 'monthly' || reportType === 'yearly' || reportType === 'custom')) return [];
    const map = new Map();
    (expensesData || []).forEach(item => {
      const key = (item.category || 'N/A').toString();
      const amt = parseFloat(item.amount) || 0;
      map.set(key, (map.get(key) || 0) + amt);
    });
    const list = Array.from(map.entries()).map(([category, amount]) => ({ category, amount }));
    // Keep insertion order (no canonical expense category list provided)
    return list;
  }, [reportType, expensesData]);

  // Format currency
  const formatCurrency = (amount) => {
    return Math.round(amount).toLocaleString('en-IN');
  };

  // Format date for display
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { 
      day: '2-digit', 
      month: 'short', 
      year: '2-digit' 
    });
  };

  // Format date as dd-mm-yyyy
  const formatDMY = (dateStr) => {
    const date = new Date(dateStr);
    const dd = String(date.getDate()).padStart(2, '0');
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const yyyy = date.getFullYear();
    return `${dd}-${mm}-${yyyy}`;
  };

  // Get report title and optional subtitle (date range moved to next line when needed)
  const getReportTitleParts = () => {
    switch (reportType) {
      case 'daily':
        return { title: `Daily Report - ${formatDate(selectedDate)}`, subtitle: '' };
      case 'weekly': {
        const weekData = weeksInMonth.find(w => w.weekNumber === selectedWeek);
        const monthYear = new Date(selectedYear, selectedMonth - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
        const title = `Weekly Report - Week ${selectedWeek} - ${monthYear}`;
        const subtitle = weekData ? `(${formatDMY(weekData.startDate)} to ${formatDMY(weekData.endDate)})` : '';
        return { title, subtitle };
      }
      case 'monthly':
        return { title: `Monthly Report - ${new Date(selectedYear, selectedMonth - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}`, subtitle: '' };
      case 'yearly':
        return { title: `Yearly Report - ${selectedYear}`, subtitle: '' };
      case 'custom': {
        const title = 'Custom Report';
        const subtitle = `(${formatDMY(customStartDate)} to ${formatDMY(customEndDate)})`;
        return { title, subtitle };
      }
      default:
        return { title: 'Income & Expenses Report', subtitle: '' };
    }
  };

  // Generate PDF
  const generatePDF = () => {
    setGenerating(true);
    
    const pdfContent = createPDFContent();
    const printWindow = window.open('', '_blank');
    printWindow.document.write(pdfContent);
    printWindow.document.close();
    
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
      setGenerating(false);
    }, 1000);
  };

  // Create PDF content
  const createPDFContent = () => {
    const { title: reportTitle, subtitle: reportSubtitle } = getReportTitleParts();
    
    return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <title>${reportTitle} - ${shopName}</title>
      <style>
        @page { margin: 15mm; size: A4 portrait; }
        body { font-family: Arial, sans-serif; font-size: 12px; margin: 0; padding: 0; }
        .report-header { text-align: center; margin-bottom: 15px; border-bottom: 2px solid #000; padding-bottom: 8px; }
        .shop-name { font-size: 18px; font-weight: bold; margin-bottom: 4px; }
        .report-title { font-size: 15px; font-weight: bold; margin-bottom: 4px; }
        .report-date { font-size: 12px; color: #666; display: none; }
        .report-subtitle { font-size: 12px; margin-top: 3px; }
        .footer { position: fixed; bottom: 10mm; left: 15mm; right: 15mm; text-align: right; font-size: 11px; }
        .data-section { margin: 15px 0; }
        .section-title { font-size: 14px; font-weight: bold; margin-bottom: 8px; border-bottom: 1px solid #000; padding-bottom: 3px; }
        .data-table { width: 100%; border-collapse: collapse; margin-bottom: 15px; font-size: 12px; }
        .data-table th, .data-table td { border: 1px solid #000; padding: 6px 5px; text-align: center; }
        .data-table th { background-color: #f0f0f0; font-weight: bold; text-align: center; }
        .data-table th:first-child { width: 60px; }
        .data-table td:first-child { width: 60px; }
        .amount { text-align: center; }
        .total-row { font-weight: bold; background-color: #f9f9f9; }
        .total-row td { text-align: center; }
        .positive { color: #008000; }
        .negative { color: #ff0000; }
        .page-break { page-break-before: always; }
      </style>
    </head>
    <body>
      <div class="report-header">
        <div class="shop-name">${shopName}</div>
        <div class="report-title">${reportTitle}</div>
        ${reportSubtitle ? `<div class="report-subtitle">${reportSubtitle}</div>` : ''}
        <div class="report-date">Generated on: ${new Date().toLocaleDateString('en-GB')}</div>
      </div>

      <div class="data-section">
        <div class="section-title">OTHER INCOME DETAILS</div>
        <table class="data-table">
          <thead>
            <tr>
              <th>S.No</th>
              <th>Category</th>
              ${reportType === 'daily' ? '<th>Description</th>' : ''}
              <th>Amount (₹)</th>
            </tr>
          </thead>
          <tbody>
            ${((reportType === 'weekly' || reportType === 'monthly' || reportType === 'yearly')
                ? aggregatedIncomeByCategory
                : incomeData.map(i => ({ category: (i.source || i.category || 'N/A'), amount: i.amount || 0, description: i.description || '-' }))
              ).map((row, index) => `
              <tr>
                <td>${index + 1}</td>
                <td>${row.category}</td>
                ${reportType === 'daily' ? `<td>${row.description || '-'}</td>` : ''}
                <td class="amount">${formatCurrency(row.amount || 0)}</td>
              </tr>
            `).join('')}
            ${((reportType === 'weekly' || reportType === 'monthly' || reportType === 'yearly') ? aggregatedIncomeByCategory.length === 0 : incomeData.length === 0) ? `<tr><td colspan="${reportType === 'daily' ? '4' : '3'}" style="text-align: center;">No income records found</td></tr>` : ''}
            <tr class="total-row">
              <td colspan="${reportType === 'daily' ? '3' : '2'}"><strong>TOTAL OTHER INCOME</strong></td>
              <td class="amount"><strong>${formatCurrency(totals.totalIncome)}</strong></td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="page-break"></div>

      <div class="report-header">
        <div class="shop-name">${shopName}</div>
        <div class="report-title">${reportTitle}</div>
        ${reportSubtitle ? `<div class="report-subtitle">${reportSubtitle}</div>` : ''}
        <div class="report-date">Generated on: ${new Date().toLocaleDateString('en-GB')}</div>
      </div>

      <div class="data-section">
        <div class="section-title">EXPENSES DETAILS</div>
        <table class="data-table">
          <thead>
            <tr>
              <th>S.No</th>
              <th>Category</th>
              ${reportType === 'daily' ? '<th>Description</th>' : ''}
              <th>Amount (₹)</th>
            </tr>
          </thead>
          <tbody>
            ${((reportType === 'weekly' || reportType === 'monthly' || reportType === 'yearly')
                ? aggregatedExpensesByCategory
                : expensesData.map(i => ({ category: (i.category || 'N/A'), amount: i.amount || 0, description: i.description || '-' }))
              ).map((row, index) => `
              <tr>
                <td>${index + 1}</td>
                <td>${row.category}</td>
                ${reportType === 'daily' ? `<td>${row.description || '-'}</td>` : ''}
                <td class="amount">${formatCurrency(row.amount || 0)}</td>
              </tr>
            `).join('')}
            ${((reportType === 'weekly' || reportType === 'monthly' || reportType === 'yearly') ? aggregatedExpensesByCategory.length === 0 : expensesData.length === 0) ? `<tr><td colspan="${reportType === 'daily' ? '4' : '3'}" style="text-align: center;">No expense records found</td></tr>` : ''}
            <tr class="total-row">
              <td colspan="${reportType === 'daily' ? '3' : '2'}"><strong>TOTAL EXPENSES</strong></td>
              <td class="amount"><strong>${formatCurrency(totals.totalExpenses)}</strong></td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="footer">Generated on: ${new Date().toLocaleDateString('en-GB')}</div>
    </body>
    </html>
    `;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[50vh]">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <span className="text-muted-foreground">Loading income & expenses data...</span>
        </div>
      </div>
    );
  }

  return (
      <main className="w-full px-4 py-8 space-y-8 max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="space-y-2 text-left">
          <h1 className="text-4xl font-bold text-left">Income & Expenses Report</h1>
          <p className="text-base text-muted-foreground text-left">
            Select a report type and date range to view detailed income and expense reports
          </p>
        </div>

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <div className="flex items-center justify-between">
                <span>{error}</span>
                <Button variant="outline" size="sm" onClick={fetchData}>Retry</Button>
              </div>
            </AlertDescription>
          </Alert>
        )}

        {/* Date Range Selector Component */}
        <DateRangeSelector
          reportType={dateSelector.reportType}
          onReportTypeChange={dateSelector.setReportType}
          selectedDateObj={dateSelector.selectedDateObj}
          onDateSelect={dateSelector.handleDateSelect}
          selectedYear={dateSelector.selectedYear}
          onYearChange={dateSelector.handleYearChange}
          selectedMonth={dateSelector.selectedMonth}
          onMonthChange={dateSelector.handleMonthChange}
          selectedWeek={dateSelector.selectedWeek}
          onWeekChange={dateSelector.handleWeekChange}
          weeksInMonth={dateSelector.weeksInMonth}
          customStartDateObj={dateSelector.customStartDateObj}
          customEndDateObj={dateSelector.customEndDateObj}
          onCustomRangeSelect={dateSelector.handleCustomRangeSelect}
          onGenerateReport={generatePDF}
          loading={generating}
          generateButtonLabel="Generate PDF Report"
          getMaxDate={dateSelector.getCurrentBusinessDateObject}
        />

        {/* Report Title */}
        {(incomeData.length > 0 || expensesData.length > 0) && (
          <div className="text-center py-4">
            <h2 className="text-2xl font-bold">{(() => {
              const { title } = getReportTitleParts();
              return title;
            })()}</h2>
            {(() => {
              const { subtitle } = getReportTitleParts();
              return subtitle && <p className="text-muted-foreground mt-1">{subtitle}</p>;
            })()}
          </div>
        )}

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card className="border-green-200 bg-green-50/50">
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <div className="p-2 rounded-lg bg-green-100">
                  <TrendingUp className="h-5 w-5 text-green-600" />
                </div>
                <CardTitle className="text-base">Total Other Income</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-green-700">₹{formatCurrency(totals.totalIncome)}</div>
              <p className="text-sm text-green-600 mt-1">{incomeData.length} entries</p>
            </CardContent>
          </Card>

          <Card className="border-red-200 bg-red-50/50">
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <div className="p-2 rounded-lg bg-red-100">
                  <TrendingDown className="h-5 w-5 text-red-600" />
                </div>
                <CardTitle className="text-base">Total Expenses</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-red-700">₹{formatCurrency(totals.totalExpenses)}</div>
              <p className="text-sm text-red-600 mt-1">{expensesData.length} entries</p>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-green-600" />
                Other Income Details
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-16 text-left">S.No</TableHead>
                    <TableHead className="text-left">Category</TableHead>
                    <TableHead className="text-left">Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(reportType === 'weekly' || reportType === 'monthly' || reportType === 'yearly' || reportType === 'custom' ? aggregatedIncomeByCategory : orderedDailyIncome).map((item, index) => (
                    <TableRow key={`income-${index}`}>
                      <TableCell className="text-left">{index + 1}</TableCell>
                      <TableCell className="text-left">{(reportType === 'weekly' || reportType === 'monthly' || reportType === 'yearly' || reportType === 'custom') ? item.category : (item.source || 'N/A')}</TableCell>
                      <TableCell className="text-left text-green-600 font-medium">₹{formatCurrency(item.amount || 0)}</TableCell>
                    </TableRow>
                  ))}
                  {((reportType === 'weekly' || reportType === 'monthly' || reportType === 'yearly' || reportType === 'custom') ? aggregatedIncomeByCategory.length === 0 : orderedDailyIncome.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={3} className="text-left text-muted-foreground">No income records found</TableCell>
                    </TableRow>
                  )}
                </TableBody>
                <TableFooter>
                  <TableRow>
                    <TableCell colSpan={2} className="text-left font-bold">TOTAL</TableCell>
                    <TableCell className="text-left font-bold text-green-600">₹{formatCurrency(totals.totalIncome)}</TableCell>
                  </TableRow>
                </TableFooter>
              </Table>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <TrendingDown className="h-5 w-5 text-red-600" />
                Expenses Details
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-16 text-left">S.No</TableHead>
                    <TableHead className="text-left">Category</TableHead>
                    <TableHead className="text-left">Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(reportType === 'weekly' || reportType === 'monthly' || reportType === 'yearly' || reportType === 'custom' ? aggregatedExpensesByCategory : expensesData).map((item, index) => (
                    <TableRow key={`expense-${index}`}>
                      <TableCell className="text-left">{index + 1}</TableCell>
                      <TableCell className="text-left">{item.category || 'N/A'}</TableCell>
                      <TableCell className="text-left text-red-600 font-medium">₹{formatCurrency(item.amount || 0)}</TableCell>
                    </TableRow>
                  ))}
                  {((reportType === 'weekly' || reportType === 'monthly' || reportType === 'yearly' || reportType === 'custom') ? aggregatedExpensesByCategory.length === 0 : expensesData.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={3} className="text-left text-muted-foreground">No expense records found</TableCell>
                    </TableRow>
                  )}
                </TableBody>
                <TableFooter>
                  <TableRow>
                    <TableCell colSpan={2} className="text-left font-bold">TOTAL</TableCell>
                    <TableCell className="text-left font-bold text-red-600">₹{formatCurrency(totals.totalExpenses)}</TableCell>
                  </TableRow>
                </TableFooter>
              </Table>
            </CardContent>
          </Card>
        </div>

    </main>
  );
}

export default IncomeExpensesReport;